import { useMutation, useQuery } from '@apollo/client/react';

import {
  BillingPlanSummaryDocument,
  type BillingPlanSummaryQuery,
  CreateBillingPortalSessionDocument,
  type CreateBillingPortalSessionMutation,
  DataTestIds,
} from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { toast } from '@repo/sds/utils/toast';

import './billing.scss';
import { BillingHistorySection } from './billing-history-section';
import { PaymentMethodsSection } from './payment-methods-section';
import { useBillingReceipt } from './use-billing-receipt';
import { YourPackageSection } from './your-package-section';

function describeBillingError(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

function useBillingPortal() {
  const [createBillingPortalSession, { loading: isPortalLoading }] = useMutation<CreateBillingPortalSessionMutation>(
    CreateBillingPortalSessionDocument,
  );

  const openPortal = async (): Promise<void> => {
    try {
      const result = await createBillingPortalSession();
      const portalUrl = result.data?.createMyBillingPortalSession;

      if (!portalUrl) {
        toast.error('Failed to open Stripe billing portal: No billing portal URL was returned');
        return;
      }

      window.location.assign(portalUrl);
    } catch (error) {
      toast.error(`Failed to open Stripe billing portal: ${describeBillingError(error)}`);
    }
  };

  return { isPortalLoading, openPortal };
}

function BillingHeader() {
  return <SDSText text="Billing" type="h1" dataTestId={DataTestIds.ADMIN.SIDEBAR.PAGE_TITLE} />;
}

export function BillingPage() {
  // Read from the cache the package card already filled: the Stripe handoff
  // copy differs for a plan that still has instalments to charge, and asking
  // again for one sentence is not worth a second round trip.
  const { data: planData } = useQuery<BillingPlanSummaryQuery>(BillingPlanSummaryDocument, {
    fetchPolicy: 'cache-first',
  });
  const hasScheduledPayments = Boolean(planData?.myBillingPlanSummary?.nextPaymentDueAt);
  const { isPortalLoading, openPortal } = useBillingPortal();

  const onOpenPortal = (): void => {
    void openPortal();
  };

  // The portal is the fallback rather than the destination now: a receipt the
  // provider actually holds opens directly, and only a payment with none sends
  // someone to the portal to look for it.
  const { isReceiptLoading, openHistoryEntryReceipt, openPlanReceipt } = useBillingReceipt({
    onUnavailable: onOpenPortal,
  });

  return (
    <SDSFlexbox className="BillingSettingsPage" direction="column" gap="card-large">
      <BillingHeader />

      <YourPackageSection
        isPortalLoading={isPortalLoading}
        onOpenPortal={onOpenPortal}
        isReceiptLoading={isReceiptLoading}
        onViewReceipt={() => {
          void openPlanReceipt();
        }}
      />

      <PaymentMethodsSection
        isPortalLoading={isPortalLoading}
        hasBillingPlan={Boolean(planData?.myBillingPlanSummary)}
        hasScheduledPayments={hasScheduledPayments}
        onOpenPortal={onOpenPortal}
      />

      <BillingHistorySection
        isPortalLoading={isPortalLoading}
        isReceiptLoading={isReceiptLoading}
        onViewReceipt={entryId => {
          void openHistoryEntryReceipt(entryId);
        }}
      />
    </SDSFlexbox>
  );
}
